import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import {
  ChimeSDKMessagingClient,
  SendChannelMessageCommand,
  ListChannelMessagesCommand,
} from "@aws-sdk/client-chime-sdk-messaging";
import {
  DefaultMessagingSession,
  ConsoleLogger,
  LogLevel,
  MessagingSessionConfiguration,
} from "amazon-chime-sdk-js";

import { IMessage, ICustomMetadata } from "../interfaces/Chat";
import { ChimeCredentials } from "../context/ChatContext";

interface CurrentUser {
  uuid?: string;
  name?: string;
}

interface ChimeSender {
  Arn?: string;
  Name?: string;
}

interface ChimeSocketPayload {
  ChannelArn?: string;
  MessageId?: string;
  Content?: string;
  CreatedTimestamp?: string;
  Metadata?: string;
  Sender?: ChimeSender;
}

interface ChimeSocketMessage {
  type?: string;
  payload?: string;
}

interface ChimeChannelMessage {
  MessageId?: string;
  Content?: string;
  CreatedTimestamp?: string;
  Metadata?: string;
  Sender?: ChimeSender;
}

type MessagesByChannel = Record<string, IMessage[]>;

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const parseJson = <T,>(value: string | undefined): T | undefined => {
  if (!value) return undefined;
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
};

const getMetadataMimeType = (metadata?: ICustomMetadata): string | undefined => {
  if (!metadata || !isRecord(metadata)) return undefined;

  const possibleType = metadata.type;
  if (typeof possibleType === "string") return possibleType;

  const mimeType = metadata.mimeType;
  if (typeof mimeType === "string") return mimeType;

  const fileType = metadata.fileType;
  if (typeof fileType === "string") return fileType;

  return undefined;
};

const getDataType = (metadata?: ICustomMetadata): "TEXT" | "IMAGE" | "VIDEO" | "DOCUMENT" => {
  const mimeType = getMetadataMimeType(metadata);

  if (!mimeType) return "TEXT";
  if (mimeType.startsWith("image/")) return "IMAGE";
  if (mimeType.startsWith("video/")) return "VIDEO";
  return "DOCUMENT";
};

const getSenderType = (senderArn?: string, senderName?: string): "patient" | "user" | "system" => {
  if (!senderArn) {
    if (senderName === "COORDINATOR") return "system";
    return "user";
  }

  const senderId = senderArn.split("/user/").pop();

  if (senderId?.startsWith("P-")) return "patient";
  if (senderId?.startsWith("U-")) return "user";
  if (senderId === "COORDINATOR") return "system";

  return "user";
};

const normalizeMetadata = (metadata: unknown): ICustomMetadata | undefined => {
  if (!isRecord(metadata)) return undefined;
  return metadata as ICustomMetadata;
};

const createMessageFromSocketPayload = (payload: ChimeSocketPayload): IMessage => {
  const metadata = normalizeMetadata(parseJson<unknown>(payload.Metadata));
  const senderArn = payload.Sender?.Arn;
  const senderName = payload.Sender?.Name;

  return {
    id: payload.MessageId || "",
    sender: senderName || "",
    content: payload.Content || "",
    timestamp: payload.CreatedTimestamp
      ? new Date(payload.CreatedTimestamp).toISOString()
      : new Date().toISOString(),
    dataType: getDataType(metadata),
    dataURL: "",
    customMetadata: metadata,
    senderType: getSenderType(senderArn, senderName),
    localId: metadata?.localId,
  };
};

const createMessageFromChannelMessage = (msg: ChimeChannelMessage): IMessage => {
  const metadata = normalizeMetadata(parseJson<unknown>(msg.Metadata));
  const senderArn = msg.Sender?.Arn;
  const senderName = msg.Sender?.Name;

  return {
    id: msg.MessageId || "",
    sender: senderName || "",
    content: msg.Content || "",
    timestamp: msg.CreatedTimestamp
      ? new Date(msg.CreatedTimestamp).toISOString()
      : new Date().toISOString(),
    dataType: getDataType(metadata),
    dataURL: "",
    customMetadata: metadata,
    senderType: getSenderType(senderArn, senderName),
    localId: metadata?.localId,
  };
};

const sortMessages = (items: IMessage[]) => {
  return [...items].sort(
    (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
  );
};

const useChat = (
  channelArn: string | null,
  getCredentials: () => Promise<ChimeCredentials>,
  currentUser: CurrentUser,
) => {
  const [messagesByChannel, setMessagesByChannel] = useState<MessagesByChannel>({});
  const messagingSessionRef = useRef<DefaultMessagingSession | null>(null);
  const reconnectTimeout = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);
  const unmounted = useRef(false);
  const channelArnRef = useRef<string | null>(null);
  const currentUserRef = useRef<CurrentUser>(currentUser);

  const messages = useMemo(() => {
    if (!channelArn) return [];
    return messagesByChannel[channelArn] || [];
  }, [messagesByChannel, channelArn]);

  const setChannelMessages = useCallback((targetChannelArn: string, updater: (prev: IMessage[]) => IMessage[]) => {
    setMessagesByChannel((prev) => ({
      ...prev,
      [targetChannelArn]: updater(prev[targetChannelArn] || []),
    }));
  }, []);

  const createClient = useCallback(async () => {
    const credentials = await getCredentials();
    return new ChimeSDKMessagingClient({
      region: credentials.region,
      credentials: {
        accessKeyId: credentials.access_key_id,
        secretAccessKey: credentials.secret_access_key,
        sessionToken: credentials.session_token,
      },
    });
  }, [getCredentials]);

  useEffect(() => {
    channelArnRef.current = channelArn;
  }, [channelArn]);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  const startSessionRef = useRef<() => Promise<void>>(async () => undefined);

  const scheduleReconnect = useCallback(() => {
    if (unmounted.current) return;
    reconnectAttempts.current += 1;
    const delay = Math.min(30000, 1000 * 2 ** reconnectAttempts.current);

    console.log(
      `Reconnecting WebSocket in ${delay}ms (attempt ${reconnectAttempts.current})`,
    );

    if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);

    reconnectTimeout.current = window.setTimeout(() => {
      void startSessionRef.current();
    }, delay);
  }, []);

  const startSession = useCallback(async () => {
    if (!channelArn) return;

    if (messagingSessionRef.current) {
      messagingSessionRef.current.stop();
      messagingSessionRef.current = null;
    }

    try {
      const logger = new ConsoleLogger("ChimeMessagingLogs", LogLevel.ERROR);
      const client = await createClient();
      const credentials = await getCredentials();
      const region = credentials.app_instance_user_arn.split(":")[3];
      const endpoint = `messaging-chime.${region}.amazonaws.com`;

      const configuration = new MessagingSessionConfiguration(
        credentials.app_instance_user_arn,
        `session-${Date.now()}`,
        endpoint,
        client,
      );

      const session = new DefaultMessagingSession(configuration, logger);
      messagingSessionRef.current = session;

      session.addObserver({
        messagingSessionDidStart: () => {
          console.log("WebSocket connected");
          reconnectAttempts.current = 0;
        },
        messagingSessionDidStop: (status: unknown) => {
          console.log("WebSocket stopped", status);
          if (messagingSessionRef.current !== session) {
            console.log("Ignoring stop event for previous chat session.");
            return;
          }
          if (!unmounted.current) scheduleReconnect();
        },
        messagingSessionDidReceiveMessage: (message: unknown) => {
          if (!isRecord(message)) return;

          const socketMessage = message as ChimeSocketMessage;
          if (socketMessage.type !== "CREATE_CHANNEL_MESSAGE") return;

          const payload = parseJson<ChimeSocketPayload>(socketMessage.payload);
          if (!payload) {
            console.warn("Invalid message payload", socketMessage.payload);
            return;
          }

          if (!payload.ChannelArn || payload.ChannelArn !== channelArnRef.current) return;

          const newMessage = createMessageFromSocketPayload(payload);

          setChannelMessages(payload.ChannelArn, (prev) => {
            if (prev.some((m) => m.id === newMessage.id)) return prev;
            return sortMessages([...prev, newMessage]);
          });
        },
      });

      session.start();
    } catch (err) {
      console.warn("Messaging session failed:", err);
      scheduleReconnect();
    }
  }, [channelArn, createClient, getCredentials, scheduleReconnect, setChannelMessages]);

  useEffect(() => {
    startSessionRef.current = startSession;
  }, [startSession]);

  useEffect(() => {
    unmounted.current = false;
    void startSessionRef.current();

    return () => {
      unmounted.current = true;
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      messagingSessionRef.current?.stop();
    };
  }, [channelArn]);

  useEffect(() => {
    if (!channelArn) return;

    const load = async () => {
      try {
        const client = await createClient();
        const credentials = await getCredentials();
        const response = await client.send(
          new ListChannelMessagesCommand({
            ChannelArn: channelArn,
            ChimeBearer: credentials.app_instance_user_arn,
          }),
        );

        const formatted: IMessage[] = (response.ChannelMessages || [])
          .map((msg) => createMessageFromChannelMessage(msg as ChimeChannelMessage))
          .filter((msg) => Boolean(msg.id));

        setChannelMessages(channelArn, (prev) => {
          const merged = [...formatted];

          prev.forEach((existing) => {
            if (!merged.some((m) => m.id === existing.id)) {
              merged.push(existing);
            }
          });

          return sortMessages(merged);
        });
      } catch (error) {
        console.warn("Failed to load messages from channel:", error);
        setChannelMessages(channelArn, () => []);
      }
    };

    void load();
  }, [channelArn, createClient, getCredentials, setChannelMessages]);

  const createMessage = useCallback(
    async (content: string, customMetadata?: ICustomMetadata) => {
      if (!channelArn) return;

      try {
        const client = await createClient();
        const credentials = await getCredentials();

        await client.send(
          new SendChannelMessageCommand({
            ChannelArn: channelArn,
            ChimeBearer: credentials.app_instance_user_arn,
            Content: content,
            Type: "STANDARD",
            Persistence: "PERSISTENT",
            Metadata: customMetadata ? JSON.stringify(customMetadata) : undefined,
          }),
        );
      } catch (err) {
        console.warn("Failed to send message:", err);
        throw err;
      }
    },
    [channelArn, getCredentials, createClient],
  );

  void currentUserRef;

  return { messages, createMessage };
};

export default useChat;